import type { LDFlagSet } from "launchdarkly-react-client-sdk";
import type { IBrandGroup } from "helpers/transforms/entities";
import type { ILocationGroupSummary } from "interfaces/location_groups";
import type { IDateRange } from "types/dates";
import type { RequestStatus } from "types/request";

enum LoadingStatus {
  NOT_LOADED = "NOT_LOADED",
  LOADED = "LOADED",
  FAILED = "FAILED",
}

interface IAppLoadingStatus {
  [key: string]: LoadingStatus;
}

interface IAppMessageStatus {
  [key: string]: LoadingStatus;
}

interface IAppAPITokenStatus {
  inventory: LoadingStatus;
  inventorySettings: LoadingStatus;
}

interface IAppAPIToken {
  inventory: string | null;
  inventorySettings: string | null;
}

interface IAccessRequests {
  workforce: boolean;
  workforceRequestStatus: RequestStatus;
}

interface IToolbarSnack {
  id: string;
  message: string | null;
  type: "info" | "error" | "success";
  timeout?: number;
}

interface IContext {
  accessRequests: IAccessRequests;
  appAPIToken: IAppAPIToken;
  appAPITokenStatus: IAppAPITokenStatus;
  appLoadingStatus: IAppLoadingStatus;
  appMessageStatus: IAppMessageStatus;
  dateRange: IDateRange;
  dateRangeFuture: IDateRange;
  entities: IBrandGroup[];
  locationGroups: ILocationGroupSummary[];
  featureFlags: LDFlagSet;
  storeKey: string | null;
  // Self-routed features track their routed path here
  selfRouted: string;
  toolbarSnacks: IToolbarSnack[];
  userApps: string[];
}

export const LD_FEATURE_FLAGS = {
  groupManagement: "groupManagement",
  hasHq3Enabled: "hasHq3Enabled",
  hq3AutoLogoutOnSubframeLogout: "hq3AutoLogoutOnSubframeLogout",
  hq3LspayTracking: "hq3LspayTracking",
  hq3ReferAndEarn: "hq3ReferAndEarn",
  hqUiIntegrationPartners: "hqUiIntegrationPartners",
  hq3UiUnifiedUserExperience: "hq3UiUnifiedUserExperience",
  hq3BillingStatusBanner: "hq3BillingStatusBanner",
  preOrderView: "preOrderView",
  workforceClockinHq3Page: "workforceClockinHq3Page",
  workforceDayView: "workforceDayView",
  workforceScheduleAndSettings: "workforceScheduleAndSettings",
  workforceScheduleTeaser: "workforceScheduleTeaser",
  neuraFlashChatBotWidget: "neuraFlashChatBotWidget",
  digitalGiftCards: "digitalGiftCards",
  digitalGiftCardPromotionalGiftCards: "digitalGiftCardPromotionalGiftCards",
  displayLiveagentChatButton: "displayLiveagentChatButton",
  showServersSalesByCheckFirst: "showServersSalesByCheckFirst",
  lspdCapitalOffer: "lspdCapitalOffer",
  lspdCapitalMerchantPortal: "lspdCapitalMerchantPortal",
  updateLiveChatButton: "updateLiveChatButton",
  liveChatButtonV2Sandbox: "liveChatButtonV2Sandbox",
  hq3CustomReports: "hq3CustomReports",
  intercom: "intercom",
  payinPayoutReasons: "payinPayoutReasons",
  auditLogPOS: "auditLogPos",
  auditLogChecksCategory: "auditLogChecksCategory",
  auditLogServersCategory: "auditLogServersCategory",
  auditLogDevicesCategory: "auditLogDevicesCategory",
  auditLogPrintersCategory: "auditLogPrintersCategory",
  upserveHqRebranding: "upserveHqRebranding",
  upservePaymentsPortal: "upservePaymentsPortal",
  creditCardSurcharging: "creditCardSurcharging",
} as const;

export const HQ_FEATURE_FLAGS = {
  adyenMerchantPortal: "adyenMerchantPortal",
  upserveAdyenOnboarding: "upserveAdyenOnboarding",
} as const;

export type FlagObject<FlagKeys extends { [key: string]: string }> = {
  [key in keyof FlagKeys]: boolean;
};

declare module "launchdarkly-react-client-sdk" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface LDFlagSet extends FlagObject<typeof LD_FEATURE_FLAGS> {
    // Prevent eslint from changing it to type
  }
}

export type HQFlags = FlagObject<typeof HQ_FEATURE_FLAGS>;

export type FeatureFlags = LDFlagSet & HQFlags;

export { IContext, IToolbarSnack, LoadingStatus };
